import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  TrendingUp, Users, DollarSign, Calendar, 
  ChevronRight, Calculator, Phone, Mail,
  Sparkles, Target, Gift, Zap
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Dashboard de Potencial de Ingresos
 * Muestra el cálculo del "potencial oculto" de un negocio
 * Usado tanto por Admin/SubAdmin como por negocios
 */
export const PotentialRevenueDashboard = ({ 
  businessId, 
  adminMode = false,
  adminId = null,
  subAdminId = null,
  onBookConsultation = null
}) => {
  const { i18n } = useTranslation();
  const isSpanish = i18n.language?.startsWith('es');
  
  const [loading, setLoading] = useState(true);
  const [potential, setPotential] = useState(null);
  const [calculating, setCalculating] = useState(false);
  
  // Form para calcular manualmente
  const [showCalculator, setShowCalculator] = useState(false);
  const [contacts, setContacts] = useState(500);
  const [ticket, setTicket] = useState(35);
  const [businessType, setBusinessType] = useState('general');

  const content = {
    es: {
      title: "Dashboard de Potencial",
      subtitle: "Descubre cuánto dinero tienes escondido en tu base de datos",
      hiddenRevenue: "Ingresos Ocultos Estimados",
      hiddenRevenueDesc: "Lo que podrías generar activando tu lista",
      breakdown: "Desglose por Estrategia",
      reactivation: "Reactivación Inmediata",
      reactivationDesc: "WhatsApp a clientes inactivos (1-7 días)",
      birthday: "Club de Cumpleaños",
      birthdayDesc: "Felicitaciones automáticas (anual)",
      newsletter: "Newsletter Semanal",
      newsletterDesc: "Mantén tu negocio en mente (anual)",
      totalFirstYear: "Total Potencial Primer Año",
      recommendations: "Servicios Recomendados",
      calculate: "Calcular mi Potencial",
      calculating: "Calculando...",
      bookConsultation: "Reservar Consultoría Gratuita",
      noData: "Aún no hay datos de potencial",
      noDataDesc: "Sube tus contactos o calcula manualmente",
      contacts: "Número de contactos",
      avgTicket: "Ticket promedio (€)",
      businessTypeLabel: "Tipo de negocio",
      types: {
        general: "General",
        restaurant: "Restaurante",
        retail: "Tienda",
        beauty: "Belleza/Peluquería",
        services: "Servicios"
      },
      roi: "ROI Estimado",
      priority: "Prioridad",
      reason: "Por qué",
      calculatedAt: "Calculado el"
    },
    en: {
      title: "Potential Dashboard",
      subtitle: "Discover how much money is hidden in your database",
      hiddenRevenue: "Estimated Hidden Revenue",
      hiddenRevenueDesc: "What you could generate by activating your list",
      breakdown: "Breakdown by Strategy",
      reactivation: "Immediate Reactivation",
      reactivationDesc: "WhatsApp to inactive customers (1-7 days)",
      birthday: "Birthday Club",
      birthdayDesc: "Automatic congratulations (annual)",
      newsletter: "Weekly Newsletter",
      newsletterDesc: "Keep your business top of mind (annual)",
      totalFirstYear: "Total First Year Potential",
      recommendations: "Recommended Services",
      calculate: "Calculate my Potential",
      calculating: "Calculating...",
      bookConsultation: "Book Free Consultation",
      noData: "No potential data yet",
      noDataDesc: "Upload your contacts or calculate manually",
      contacts: "Number of contacts",
      avgTicket: "Average ticket (€)",
      businessTypeLabel: "Business type",
      types: {
        general: "General",
        restaurant: "Restaurant",
        retail: "Retail",
        beauty: "Beauty/Hair Salon",
        services: "Services"
      },
      roi: "Estimated ROI",
      priority: "Priority",
      reason: "Why",
      calculatedAt: "Calculated on"
    }
  };

  const c = isSpanish ? content.es : content.en;

  useEffect(() => {
    if (businessId) {
      fetchPotential();
    }
  }, [businessId]);

  const fetchPotential = async () => {
    try {
      setLoading(true);
      const headers = {};
      if (adminId) headers['X-Admin-Id'] = adminId;
      if (subAdminId) headers['X-SubAdmin-Id'] = subAdminId;
      
      const { data } = await axios.get(`${API}/upsell/potential/${businessId}`, { headers });
      
      if (data.potential_calculated && data.potential_data) {
        setPotential(data.potential_data);
      }
    } catch (err) {
      console.error('Error fetching potential:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculatePotential = async () => {
    try {
      setCalculating(true);
      const { data } = await axios.post(`${API}/upsell/calculate-potential`, null, {
        params: {
          total_contacts: contacts,
          estimated_ticket: ticket,
          business_type: businessType
        }
      });
      
      setPotential(data);
      setShowCalculator(false);
      toast.success(isSpanish ? '¡Potencial calculado!' : 'Potential calculated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error calculating potential');
    } finally {
      setCalculating(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-gray-500" data-testid="potential-loading">
        <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full mx-auto mb-4" />
        {isSpanish ? 'Cargando datos...' : 'Loading data...'}
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden" data-testid="potential-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <Sparkles className="w-6 h-6" />
          <h2 className="text-xl font-bold">{c.title}</h2>
        </div>
        <p className="text-emerald-100 text-sm">{c.subtitle}</p>
      </div>

      <div className="p-6">
        {potential ? (
          <>
            {/* Hidden Revenue Hero */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl p-6 mb-6 text-center border border-amber-200 dark:border-amber-800">
              <p className="text-sm text-amber-700 dark:text-amber-400 mb-2">{c.hiddenRevenue}</p>
              <p className="text-5xl font-bold text-amber-600 dark:text-amber-400 mb-2" data-testid="hidden-revenue">
                {formatCurrency(potential.hidden_revenue_estimate)}
              </p>
              <p className="text-xs text-amber-600/70 dark:text-amber-500">{c.hiddenRevenueDesc}</p>
            </div>

            {/* Breakdown */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
                <Target className="w-4 h-4" />
                {c.breakdown}
              </h3>
              
              <div className="space-y-3">
                {/* Reactivation */}
                <div className="flex items-center justify-between p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-800 flex items-center justify-center">
                      <Zap className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{c.reactivation}</p>
                      <p className="text-xs text-gray-500">{c.reactivationDesc}</p>
                    </div>
                  </div>
                  <p className="font-bold text-emerald-600 dark:text-emerald-400">
                    {formatCurrency(potential.reactivation_immediate)}
                  </p>
                </div>

                {/* Birthday */}
                <div className="flex items-center justify-between p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-800 flex items-center justify-center">
                      <Gift className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{c.birthday}</p>
                      <p className="text-xs text-gray-500">{c.birthdayDesc}</p>
                    </div>
                  </div>
                  <p className="font-bold text-purple-600 dark:text-purple-400">
                    {formatCurrency(potential.birthday_annual)}
                  </p>
                </div>

                {/* Newsletter */}
                <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-800 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{c.newsletter}</p>
                      <p className="text-xs text-gray-500">{c.newsletterDesc}</p>
                    </div>
                  </div>
                  <p className="font-bold text-blue-600 dark:text-blue-400">
                    {formatCurrency(potential.newsletter_potential)}
                  </p>
                </div>
              </div>

              {/* Total */}
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <span className="font-semibold text-gray-900 dark:text-white">{c.totalFirstYear}</span>
                <span className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(potential.total_first_year)}
                </span>
              </div>
            </div>

            {/* Recommendations */}
            {potential.service_recommendations?.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  {c.recommendations}
                </h3>
                
                <div className="space-y-2">
                  {potential.service_recommendations.map((rec, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center text-sm font-bold text-emerald-600">
                          {rec.priority}
                        </div>
                        <div>
                          <p className="font-medium text-sm text-gray-900 dark:text-white">{rec.name}</p>
                          <p className="text-xs text-gray-500">{rec.reason}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-emerald-600">{rec.roi_estimate}</p>
                        <p className="text-xs text-gray-500">{rec.price}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CTA */}
            {onBookConsultation && (
              <Button 
                onClick={onBookConsultation}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="book-consultation-btn"
              >
                <Phone className="w-4 h-4 mr-2" />
                {c.bookConsultation}
              </Button>
            )}

            {/* Calculated timestamp */}
            {potential.calculated_at && (
              <p className="text-xs text-gray-400 text-center mt-4">
                {c.calculatedAt}: {new Date(potential.calculated_at).toLocaleDateString()}
              </p>
            )}
          </>
        ) : (
          /* No data - show calculator */
          <div className="text-center py-8">
            <Calculator className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400 mb-2">{c.noData}</p>
            <p className="text-sm text-gray-500 mb-6">{c.noDataDesc}</p>
            
            {!showCalculator ? (
              <Button 
                onClick={() => setShowCalculator(true)}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="show-calculator-btn"
              >
                <Calculator className="w-4 h-4 mr-2" />
                {c.calculate}
              </Button>
            ) : (
              <div className="max-w-sm mx-auto space-y-4 text-left">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {c.contacts}
                  </label>
                  <input
                    type="number"
                    value={contacts}
                    onChange={(e) => setContacts(parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    data-testid="contacts-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {c.avgTicket}
                  </label>
                  <input
                    type="number"
                    value={ticket}
                    onChange={(e) => setTicket(parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    data-testid="ticket-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {c.businessTypeLabel}
                  </label>
                  <select
                    value={businessType}
                    onChange={(e) => setBusinessType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    data-testid="business-type-select"
                  >
                    {Object.entries(c.types).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
                
                <Button 
                  onClick={calculatePotential}
                  disabled={calculating || contacts < 1}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid="calculate-btn"
                >
                  {calculating ? c.calculating : c.calculate}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PotentialRevenueDashboard;
